import { OrderedMap } from "immutable";
import useLayoutVariant from "components/common/PaginatedEntityTable/hooks/useLayoutVariant";
import useUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUserLayoutPreferences";
import { useSearchAssets } from "security-app/hooks/useAssetsAPI";
import AssetFilterValuesFilter from "./AssetFilterValuesFilter";
export const ASSETS_VIEWS = {
  ALL: "",
  MACHINES: "machine",
  USERS: "user"
};
export const ASSETS_VIEWS_VARIANTS = [
  {
    label: "All",
    value: ASSETS_VIEWS.ALL
  },
  {
    label: "Machines",
    value: ASSETS_VIEWS.MACHINES
  },
  {
    label: "Users",
    value: ASSETS_VIEWS.USERS
  }
];
export const ASSETS_TABLE_ID = "assets";
const COMMON_COLUMNS = ["name", "risk_score_normalized", "priority", "category"];
const ALL_COLUMNS = [...COMMON_COLUMNS, "type"];
const MACHINE_COLUMNS = [
  ...COMMON_COLUMNS,
  "hostnames",
  "ip_addresses",
  "mac_addresses",
  "vulnerabilities"
];
const USER_COLUMNS = [
  ...COMMON_COLUMNS,
  "usernames",
  "user_ids",
  "email_addresses",
  "first_name",
  "last_name"
];
const FILTER_VALUE_ATTRIBUTES = /* @__PURE__ */ new Set([
  "name",
  "category",
  "hostnames",
  "ip_addresses",
  "mac_addresses",
  "owner",
  "usernames",
  "user_ids",
  "email_addresses",
  "first_name",
  "last_name",
  "member_of",
  "vulnerability_cve",
  "scanner_type",
  "scanner"
]);
const SLICEABLE_ATTRIBUTES = /* @__PURE__ */ new Set([
  "priority",
  "category",
  "risk_score_normalized",
  "vulnerability_cve",
  "scanner_type",
  "scanner",
  "vulnerability_cvss"
]);
const getDefaultAttributes = (layoutVariant) => {
  switch (layoutVariant) {
    case "machine":
      return MACHINE_COLUMNS;
    case "user":
      return USER_COLUMNS;
    default:
      return ALL_COLUMNS;
  }
};
export const useAssetsTableElements = () => {
  const { activeLayoutVariant } = useLayoutVariant();
  const defaultDisplayedAttributes = getDefaultAttributes(activeLayoutVariant);
  const defaultColumnOrder = defaultDisplayedAttributes;
  return {
    defaultLayout: {
      entityTableId: ASSETS_TABLE_ID,
      layoutVariant: activeLayoutVariant,
      defaultPageSize: 20,
      defaultSort: { attributeId: "risk_score_normalized", direction: "desc" },
      defaultDisplayedAttributes,
      defaultColumnOrder
    }
  };
};
export const useAssetsEntityTableDefaultFilters = (entityTableId) => {
  const { activeLayoutVariant } = useLayoutVariant();
  const {
    data: { defaultFilters }
  } = useUserLayoutPreferences(entityTableId, activeLayoutVariant);
  const layoutVariantFilters = activeLayoutVariant ? OrderedMap({ type: [activeLayoutVariant] }) : OrderedMap();
  return (defaultFilters != null ? defaultFilters : OrderedMap()).merge(layoutVariantFilters);
};
export const withAssetAttributeFlags = (response, layoutVariant) => ({
  ...response,
  attributes: response.attributes.map((attribute) => ({
    ...attribute,
    sliceable: SLICEABLE_ATTRIBUTES.has(attribute.id),
    ...FILTER_VALUE_ATTRIBUTES.has(attribute.id) && { filter_component: AssetFilterValuesFilter },
    // The type is already defined by the layout variant filter, so it should not be selectable as a column.
    // Hidden attributes are still filterable.
    hidden: attribute.hidden || !!layoutVariant && attribute.id === "type"
  }))
});
export const useFetchAssets = () => {
  const { activeLayoutVariant } = useLayoutVariant();
  const { searchAssets } = useSearchAssets();
  return async (searchParams) => withAssetAttributeFlags(await searchAssets(searchParams), activeLayoutVariant);
};
